import React from 'react';
import { DispatchProp } from 'react-redux';

import {
    ApiAdapterPromise,
    ApiAdapterRequestConfig,
    ApiResponse,
    rulesEngineSelectors,
    CharacterPreferences,
    ClassDefinitionContract,
    PrerequisiteData,
    RaceDefinitionContract,
    RaceUtils,
    RuleData,
    Race,
} from '@dndbeyond/character-rules-engine/es';

import FormInputField from '../../../../Shared/components/common/FormInputField';
import { apiCreatorSelectors } from '../../../../Shared/selectors';
import { builderActions } from '../../../actions';
import Button from '../../../components/Button';
import ClassChooser from '../../../components/ClassChooser';
import ClassDisplaySimple from '../../../components/ClassDisplaySimple';
import Page from '../../../components/Page';
import PageBody from '../../../components/PageBody';
import PageHeader from '../../../components/PageHeader';
import RaceChooser from '../../../components/RaceChooser';
import RaceDisplaySimple from '../../../components/RaceDisplaySimple';
import { RouteKey } from '../../../constants/navigation';
import { builderSelectors } from '../../../selectors';
import { BuilderAppState } from '../../../typings';
import ConnectedBuilderPage from '../ConnectedBuilderPage';

interface Props extends DispatchProp {
    loadRaces: (
        additionalConfig?: Partial<ApiAdapterRequestConfig>,
    ) => ApiAdapterPromise<ApiResponse<Array<RaceDefinitionContract>>>;
    loadClasses: (
        additionalConfig?: Partial<ApiAdapterRequestConfig>,
    ) => ApiAdapterPromise<ApiResponse<Array<ClassDefinitionContract>>>;
    hasCharRequiredData: boolean;
    ruleData: RuleData;
    preferences: CharacterPreferences;
    prerequisiteData: PrerequisiteData;
}
interface State {
    race: RaceDefinitionContract | null;
    charClass: ClassDefinitionContract | null;
    name: string;
    showRace: boolean;
    showClass: boolean;
}
class QuickBuild extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props);

        this.state = {
            race: null,
            charClass: null,
            name: '',
            showRace: false,
            showClass: false,
        };
    }

    handleSubmit = (): void => {
        const { dispatch } = this.props;
        const { race, charClass, name } = this.state;

        dispatch(
            builderActions.quickBuildRequest(
                race ? RaceUtils.getEntityRaceId(race) : null,
                race ? RaceUtils.getEntityRaceTypeId(race) : null,
                charClass ? charClass.id : null,
                name,
            ),
        );
    };

    handleRaceSelected = (race: Race): void => {
        this.setState({
            race,
        });
    };

    handleClassSelected = (item: any, amount: number, entity: any): void => {
        this.setState({
            charClass: entity,
        });
    };

    handleRemoveRace = (): void => {
        this.setState({
            race: null,
        });
    };

    handleRemoveClass = (): void => {
        this.setState({
            charClass: null,
        });
    };

    handleNameChange = (name: string): void => {
        this.setState({
            name,
        });
    };

    handleToggleRace = () => {
        this.setState((prevState) => ({
            showRace: !prevState.showRace,
        }));
    };

    handleToggleClass = () => {
        this.setState((prevState) => ({
            showClass: !prevState.showClass,
        }));
    };

    checkHasRequiredData = () => {
        const { race, charClass } = this.state;
        return race !== null && charClass !== null;
    };

    renderRaceUi = (): React.ReactNode => {
        const { race, showRace } = this.state;
        const { loadRaces, ruleData } = this.props;

        if (race === null) {
            const headerConClasses: Array<string> = ['quick-build-header'];
            if (showRace) {
                headerConClasses.push('quick-build-header-opened');
            } else {
                headerConClasses.push('quick-build-header-collapsed');
            }
            return (
                <div className="builder-field choose-race">
                    <div className={headerConClasses.join(' ')} onClick={this.handleToggleRace}>
                        <div className="quick-build-header-heading">
                            <div className="builder-field-heading">Choose Race</div>
                        </div>
                        <div className="quick-build-header-trigger" />
                    </div>
                    {showRace && (
                        <RaceChooser
                            loadRaces={loadRaces}
                            onRaceSelected={this.handleRaceSelected}
                            ruleData={ruleData}
                        />
                    )}
                </div>
            );
        } else {
            return <RaceDisplaySimple onRequestAction={this.handleRemoveRace} race={race} ruleData={ruleData} />;
        }
    };

    renderClassUi = (): React.ReactNode => {
        const { charClass, showClass } = this.state;
        const { loadClasses, preferences, prerequisiteData } = this.props;

        if (charClass === null) {
            const headerConClasses: Array<string> = ['quick-build-header'];
            if (showClass) {
                headerConClasses.push('quick-build-header-opened');
            } else {
                headerConClasses.push('quick-build-header-collapsed');
            }
            return (
                <div className="builder-field choose-class">
                    <div className={headerConClasses.join(' ')} onClick={this.handleToggleClass}>
                        <div className="quick-build-header-heading">
                            <div className="builder-field-heading">Choose Class</div>
                        </div>
                        <div className="quick-build-header-trigger" />
                    </div>
                    {showClass && (
                        <ClassChooser
                            onClassSelected={this.handleClassSelected}
                            loadClasses={loadClasses}
                            preferences={preferences}
                            prerequisiteData={prerequisiteData}
                        />
                    )}
                </div>
            );
        } else {
            return <ClassDisplaySimple onRequestChange={this.handleRemoveClass} charClass={charClass} />;
        }
    };

    render() {
        return (
            <Page clsNames={['quick-build']}>
                <PageHeader>Quick Build</PageHeader>
                <PageBody>
                    <div className="quick-build-intro">
                        To create a 1st-level character with recommended starting options, choose a race and class
                        below. You can provide a name or leave it blank to randomize one.
                    </div>
                    {this.renderRaceUi()}
                    {this.renderClassUi()}
                    <FormInputField label="Character Name" onChange={this.handleNameChange} />
                    <Button
                        size="large"
                        block={true}
                        onClick={this.handleSubmit}
                        disabled={!this.checkHasRequiredData()}
                    >
                        Create Character
                    </Button>
                </PageBody>
            </Page>
        );
    }
}

export default ConnectedBuilderPage(QuickBuild, RouteKey.QUICK_BUILD, (state: BuilderAppState) => {
    return {
        loadRaces: apiCreatorSelectors.makeLoadAvailableRaces(state),
        loadClasses: apiCreatorSelectors.makeLoadAvailableClasses(state),
        hasCharRequiredData: builderSelectors.checkHasCharRequiredData(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        preferences: rulesEngineSelectors.getCharacterPreferences(state),
        prerequisiteData: rulesEngineSelectors.getPrerequisiteData(state),
    };
});
